import React, { useState } from 'react';
import { useIntl } from 'react-intl';
import { KeenIcon } from '@/components';

interface SearchableMultiSelectProps {
  label: string;
  options: Array<{ id: string; label: string }>;
  selectedIds: string[];
  onChange: (selectedIds: string[]) => void;
  disabled?: boolean;
  placeholder?: string;
  isLoading?: boolean;
  onSearchChange?: (query: string) => void;
  onLoadMore?: () => void;
  hasMore?: boolean;
  onSelectAllToggle?: (nextAllSelected: boolean) => void;
}

const SearchableMultiSelect = ({
  label,
  options,
  selectedIds,
  onChange,
  disabled = false,
  placeholder = 'No items selected',
  isLoading = false,
  onSearchChange,
  onLoadMore,
  hasMore,
  onSelectAllToggle,
}: SearchableMultiSelectProps) => {
  const [searchQuery, setSearchQuery] = useState('');
  const intl = useIntl();
  // Generate a unique ID for this component instance
  const inputId = React.useId();

  const filteredOptions = onSearchChange
    ? options
    : options.filter((opt) =>
        opt.label.toLowerCase().includes(searchQuery.toLowerCase())
      );

  const selectedLabels = options
    .filter((opt) => selectedIds.includes(opt.id))
    .map((opt) => opt.label);

  const allOptionIds = options.map((opt) => opt.id);
  const allSelected =
    allOptionIds.length > 0 &&
    allOptionIds.every((id) => selectedIds.includes(id));

  const handleToggleOption = (optionId: string) => {
    if (disabled) return;

    const isSelected = selectedIds.includes(optionId);
    const newSelected = isSelected
      ? selectedIds.filter((id) => id !== optionId)
      : [...selectedIds, optionId];
    onChange(newSelected);
  };

  const handleRemoveTag = (
    optionId: string,
    e: React.MouseEvent<HTMLButtonElement>
  ) => {
    e.stopPropagation();
    if (disabled) return;
    handleToggleOption(optionId);
  };

  const handleToggleSelectAll = () => {
    if (disabled) return;

    const nextAllSelected = !allSelected;

    if (onSelectAllToggle) {
      onSelectAllToggle(nextAllSelected);
      return;
    }

    if (nextAllSelected) {
      onChange(allOptionIds);
    } else {
      onChange([]);
    }
  };

  const handleSearchInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchQuery(value);
    if (onSearchChange) {
      onSearchChange(value);
    }
  };

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    if (!onLoadMore || !hasMore || isLoading) return;

    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    const distanceFromBottom = scrollHeight - (scrollTop + clientHeight);

    if (distanceFromBottom < 40) {
      onLoadMore();
    }
  };

  const maxVisibleTags = 20;

  return (
    <fieldset className="space-y-2">
      <legend className="form-label">{label}</legend>

      {/* Summary box */}
      <div
        className="border border-gray-300 rounded-lg bg-gray-100 p-2 min-h-[40px] max-h-24 overflow-y-auto"
        aria-label={intl.formatMessage(
          { id: 'COMMON.SELECTED_ITEMS' },
          { label }
        )}
      >
        {selectedLabels.length === 0 ? (
          <div className="text-gray-500 contrast:text-white text-sm py-1">
            {placeholder}
          </div>
        ) : (
          <>
            <div className="flex flex-wrap gap-2">
              {selectedLabels.slice(0, maxVisibleTags).map((label) => {
                const optionId = options.find((opt) => opt.label === label)?.id;
                return (
                  <span
                    key={optionId}
                    className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 dark:bg-blue-950 contrast:bg-blue-900 text-blue-800 dark:text-white contrast:text-white text-xs rounded"
                  >
                    {label}
                    {!disabled && optionId && (
                      <button
                        type="button"
                        onClick={(e) => handleRemoveTag(optionId, e)}
                        className="hover:text-blue-600 dark:hover:text-white contrast:hover:text-white focus:outline-none"
                        aria-label={intl.formatMessage(
                          { id: 'COMMON.REMOVE_ITEM' },
                          { label }
                        )}
                      >
                        <KeenIcon icon="cross" className="text-xs" />
                      </button>
                    )}
                  </span>
                );
              })}
            </div>
            {selectedLabels.length > maxVisibleTags && (
              <div className="text-xs text-gray-600 contrast:text-white mt-1">
                {intl.formatMessage(
                  { id: 'COMMON.MORE_SELECTED' },
                  { count: selectedLabels.length - maxVisibleTags }
                )}
              </div>
            )}
          </>
        )}
      </div>

      {/* Options + search + select all */}
      <div
        className={`bg-gray-100 border border-gray-200 rounded-lg ${disabled ? 'pointer-events-none' : ''}`}
      >
        <div className="p-2 border-b border-gray-200 contrast:border-gray-600 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <label htmlFor={inputId} className="sr-only">
            {intl.formatMessage({ id: 'COMMON.SEARCH' })} {label}
          </label>
          <input
            type="text"
            id={inputId}
            placeholder={`${intl.formatMessage({ id: 'COMMON.SEARCH' })}...`}
            value={searchQuery}
            onChange={handleSearchInputChange}
            disabled={disabled}
            className="input input-sm w-full sm:flex-1 searchable-multi-select-search-input"
          />

          <label
            htmlFor={`${inputId}-select-all`}
            className="flex items-center gap-2 text-xs sm:text-sm contrast:text-white"
          >
            <input
              type="checkbox"
              id={`${inputId}-select-all`}
              className="checkbox checkbox-sm"
              checked={allSelected}
              onChange={handleToggleSelectAll}
              disabled={options.length === 0}
            />
            <span>{intl.formatMessage({ id: 'COMMON.SELECT_ALL' })}</span>
          </label>
        </div>

        <div className="max-h-64 overflow-y-auto p-2" onScroll={handleScroll}>
          {filteredOptions.length === 0 && !isLoading ? (
            <div className="p-3 text-center text-gray-600 contrast:text-white text-sm">
              {intl.formatMessage({ id: 'COMMON.NO_OPTIONS_FOUND' })}
            </div>
          ) : (
            <div className="space-y-1">
              {filteredOptions
                .filter((option) => option.label && option.label.trim() !== '')
                .map((option) => {
                  const checkboxId = `${inputId}-option-${option.id}`;
                  return (
                    <label
                      key={option.id}
                      htmlFor={checkboxId}
                      className="flex items-center gap-2 p-2 hover:bg-gray-200 rounded cursor-pointer text-sm contrast:text-white"
                    >
                      <input
                        type="checkbox"
                        id={checkboxId}
                        className="checkbox checkbox-sm cursor-pointer"
                        checked={selectedIds.includes(option.id)}
                        onChange={() => handleToggleOption(option.id)}
                      />
                      <span className="cursor-pointer">{option.label}</span>
                    </label>
                  );
                })}
            </div>
          )}

          {isLoading && (
            <div className="p-2 text-center text-gray-600 contrast:text-white text-xs">
              {intl.formatMessage({ id: 'COMMON.LOADING_OPTIONS' })}
            </div>
          )}

          {hasMore && !isLoading && filteredOptions.length > 0 && (
            <div className="p-2 text-center text-gray-600 contrast:text-white text-xs">
              {intl.formatMessage({ id: 'COMMON.SCROLL_TO_LOAD_MORE' })}
            </div>
          )}
        </div>
      </div>
    </fieldset>
  );
};

export { SearchableMultiSelect };
